// Platform wrapper around the shared capture list+form-input interface
// (@twasta/page-runtime/txn/capture). The shared component takes an injected
// CaptureRuntime (data adapter + form transport + layout persistence +
// ensureReady) and a versionKey; this wrapper curries in the designer
// runtime (embedDataApi + live-preview TxnRuntime + toast), the idempotent
// live-preview deploy, and the object-version store subscription — so every
// existing call site keeps its original props and behavior exactly.

import { useMemo, type ComponentProps } from 'react'
import { EmbedCapturePreview as CoreEmbedCapturePreview } from '@twasta/page-runtime/txn/capture'
import { useObjectVersion } from '@/store/objectVersionStore'
import { livePreviewDeploy } from '@/livepreview/api'
import { makeDesignerCaptureRuntime } from '@/livepreview/captureRuntime'

// Module-scope deploy tracker. Capture/integrate deploys the same
// schema live-preview's list does — keep it idempotent across
// component remounts the same way EmbedLivePreview does.
const deployedTxns = new Map<string, Promise<void>>()

function deployKey(project: string, objName: string): string {
  return `${project}::${objName}`
}

async function ensureDeployed(project: string, objName: string): Promise<void> {
  const key = deployKey(project, objName)
  const existing = deployedTxns.get(key)
  if (existing) return existing
  const p = livePreviewDeploy(project, objName).then(() => undefined)
  deployedTxns.set(key, p)
  try {
    await p
  } catch (err) {
    deployedTxns.delete(key)
    throw err
  }
}

type Props = Omit<
  ComponentProps<typeof CoreEmbedCapturePreview>,
  'runtime' | 'versionKey'
> & {
  // 'design' (object designer) bakes "Apply layout" into the shared core
  // design; 'customize' (7077 Run view) writes a per-tenant model overlay.
  layoutMode?: 'design' | 'customize'
}

export default function EmbedCapturePreview({ layoutMode = 'design', ...props }: Props) {
  const versionKey = useObjectVersion('T', props.project, props.embed.obj_name || '')
  const runtime = useMemo(
    () => makeDesignerCaptureRuntime(props.project, ensureDeployed, layoutMode),
    [props.project, layoutMode],
  )
  return (
    <CoreEmbedCapturePreview
      {...props}
      runtime={runtime}
      versionKey={versionKey}
    />
  )
}
